import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useAuth } from 'src/auth';
import { ORGANIZATION_SETTINGS_QUERY } from 'src/modules/entities/organization';
import type { OrganizationSettingsQuery } from 'types/graphql';
import { create } from 'zustand';

/** The same document object (refetch matches by identity); kept for the store's remaining readers (FU-RX-ST1). */
export const GET_ORG_SETTINGS = ORGANIZATION_SETTINGS_QUERY;

type OrganizationSettings = NonNullable<OrganizationSettingsQuery['organization']>;

/**
 * Compatibility adapter (P7-R36b): RoutesBridge's `useInitializeOrgSettings` mirrors the app-wide
 * settings read into this store for the accounting readers (out of bounds) and the readers queued for
 * wave G-b. Every other reader takes its section from Apollo (`src/modules/entities/organization`);
 * `orgSettings.store.readers.test.ts` keeps the reader list from growing.
 */
export type OrgSettingsStore = {
  organization?: (Omit<OrganizationSettings, 'closedAt'> & { closedAt?: string | Date | null }) | null;
  financeConfiguration?: OrganizationSettingsQuery['financeConfiguration'];
  orgSettings?: OrganizationSettingsQuery['orgSettings'];
  loaded: boolean;
  setOrgSettings: (data: Partial<OrgSettingsStore>) => void;
};

export const useOrgSettings = create<OrgSettingsStore>((set) => ({
  organization: undefined,
  financeConfiguration: undefined,
  orgSettings: undefined,
  loaded: false,
  setOrgSettings: (data) => set((state) => ({ ...state, ...data })),
}));

export const useInitializeOrgSettings = () => {
  const { isAuthenticated } = useAuth();
  const { data } = useQuery(GET_ORG_SETTINGS, { fetchPolicy: 'cache-first', skip: !isAuthenticated });
  const setOrgSettings = useOrgSettings((state) => state.setOrgSettings);

  useEffect(() => {
    if (data) {
      setOrgSettings({
        organization: data.organization,
        financeConfiguration: data.financeConfiguration,
        orgSettings: data.orgSettings,
        loaded: true,
      });
    }
  }, [data, setOrgSettings]);
};
